export const COUNTRIES = ['uz', 'kz', 'kg'] as const;
export type Country = (typeof COUNTRIES)[number];

/** Localizes a country's region names (e.g. raw Uzbek-Latin oblast names → Russian). */
export interface RegionCatalog {
  localize(name: string | null | undefined, locale: string): string | null;
}

/** Per-locale magnitude suffixes for compact budget formatting ("млрд",
 *  "mlrd", …), largest-magnitude first — paired positionally with the fixed
 *  [1e12, 1e9, 1e6, 1e3] threshold ladder in `compactMagnitude`. Keyed by the
 *  locale discriminator it selects on ('ru' | 'uz' today). */
export interface CurrencyConfig {
  readonly code: string;
  readonly symbol: string;
  readonly compactUnits: Record<string, readonly string[]>;
  /** The currency WORD shown next to amounts, per UI locale (ru → «сум»/«тенге»,
   *  uz → «so'm»). The UI historically hardcoded «сум»/«so'm» inline; it now
   *  interpolates this via the i18n `{{currency}}` token so a KZ (KZT) build reads
   *  «тенге», a UZ (UZS) build «сум»/«so'm», from one source. Keyed by the locale
   *  discriminator, like `compactUnits`. */
  readonly words: Record<string, string>;
}

/** Site identity — was hardcoded as `SITE_NAME`/`SITE_URL` literals in
 *  `seo.tsx` and `prerender.ts`, plus the support address duplicated across
 *  notification senders (`support@uztender.com`). */
export interface BrandConfig {
  readonly name: string;
  readonly siteUrl: string;
  readonly supportEmail: string;
  /** The human support contact on Telegram that the site PUBLISHES next to
   *  `supportEmail` — the requisites card, the «Помощь и поддержка» modal and
   *  /about. `null` = no Telegram contact is shown at all.
   *
   *  ⚠️ NOT the same switch as `messaging.telegram`. That one says whether the
   *  deployment runs the Telegram BOT (alerts, login widget); this one says
   *  whether a person can be written to on Telegram — owner's call 2026-10-13:
   *  «контакты на KZ — Telegram и почта», no phone. */
  readonly supportTelegram: { readonly handle: string; readonly url: string } | null;
  /** Whether the operator's PHONE line (and the WhatsApp link, which is the same
   *  number) is published on this deployment. UZ: yes. KZ: no — owner's call
   *  2026-10-13, the number is the owner's own Uzbek mobile. The legal
   *  requisites (name, address, ИНН, bank) are NOT affected by this flag. */
  readonly publishPhone: boolean;
  /** The site's accent colour, as `#rrggbb` literals. See `BrandPalette`. */
  readonly palette: BrandPalette;
}

/**
 * The deployment's accent colour — Tailwind's `brand` / `brand-mid` /
 * `brand-light` (apps/web/tailwind.config.ts reads them as CSS variables).
 *
 * The stylesheet's defaults (`:root` in apps/web/src/styles/globals.css) are
 * UzTender's teal, so UZ and KZ carry exactly those values and their pages are
 * unchanged. A deployment whose palette differs (KG, owner's call 05.11: a muted
 * crimson that sits with the red logo without shouting) gets the variables
 * overridden in its `index.html` at build time — the `brand-tokens` plugin in
 * apps/web/vite.config.ts — and its own `theme-color`. HTML letters, which
 * cannot read the site's CSS, take `primary` as a literal.
 */
export interface BrandPalette {
  /** `brand` — text, filled buttons, borders, the focus ring, `theme-color`. */
  readonly primary: string;
  /** `brand-mid` — the HOVER shade of a filled button or a brand link (every
   *  use is `hover:`). Teal goes one step LIGHTER on hover; keep that relation. */
  readonly mid: string;
  /** `brand-light` — the pale tint (auth-page glow, selected surfaces). */
  readonly light: string;
}

/** The deployment's country NAME, in the grammatical cases the copy needs. The
 *  UI hardcoded «Узбекистан»/«Узбекистана»/«Узбекистане» inline; it now
 *  interpolates these via the i18n tokens `{{country}}` (nominative),
 *  `{{countryGen}}` (genitive) and `{{countryPrep}}` (prepositional/locative),
 *  so a KZ build reads «Казахстан/Казахстана/Казахстане». Keyed by UI locale
 *  like `currency.words` / `compactUnits`. */
export interface CountryNameForms {
  /** nominative — «Казахстан» (e.g. «Республики Казахстан») */
  readonly nom: string;
  /** genitive — «Казахстана» (e.g. «госзакупки Казахстана») */
  readonly gen: string;
  /** prepositional/locative — «Казахстане» (e.g. «в Казахстане») */
  readonly prep: string;
}

/** First-party analytics identity that differs per deployment. `metaPixelId` is
 *  the Meta (Facebook) Pixel id — it was a hardcoded literal in `index.html`
 *  (UZ `1194754032777047`) and the dead `VITE_META_PIXEL_ID` env var. It now
 *  lives here so the Vite `transformIndexHtml` plugin bakes the right id per
 *  country with no env drift. The SERVER CAPI token is NOT here — it is a secret
 *  read from the per-host `.env` (`META_CAPI_ACCESS_TOKEN`), never shipped in the
 *  browser bundle. Public: the web build reads the pixel id at bundle time. */
export interface AnalyticsConfig {
  readonly metaPixelId: string;
  /** GA4 Measurement id (`G-…`). Baked into index.html per country by the Vite
   *  build, like `metaPixelId`. **Empty string disables GA4 for the deployment**
   *  (the gtag config + gtag.js load are skipped) — KZ ships `''` so KZ page
   *  views do NOT leak into UZ's GA4 property. UZ keeps its `G-06TE1QP5JR`. */
  readonly ga4MeasurementId: string;
}

/** Browser-safe company-identifier descriptor for the web ИНН/БИН form (label +
 *  expected digit count). The full `CompanyIdConfig` (with the checksum `validate`
 *  fn) stays server-only; the browser only needs the length gate + label, and the
 *  server does the real checksum on submit. UZ ИНН/СТИР = 9 digits, KZ БИН/ИИН = 12,
 *  KG ИНН = 14. */
export interface CompanyIdFormConfig {
  readonly label: string;
  readonly digits: number;
}

/** Title/code → ИКПУ (product-classifier) adapter. The UZ implementation
 *  (`uz.classifier.ts`) is a keyword heuristic; a different country plugs in its
 *  own scheme's classifier. Both methods are resolve-only — an unmatched input
 *  yields `null`, never a wrong guess. Server-side only — deliberately NOT on
 *  `PublicCountryConfig`: classification runs at ingest, and its rule tables have
 *  no business shipping in the browser bundle. `resolve` prefers a normalised
 *  source-provided `code`, falling back to classifying `title`; `classify` infers
 *  purely from a title. */
export interface ClassifierAdapter {
  /** The code system's short Russian name as the copy prints it before a code:
   *  «ЕНКТ» on Uzbekistan (the dotted UZEX code, column `tenders.ikpu`), «ЕНС ТРУ»
   *  on Kazakhstan. */
  readonly label: string;
  /** The bucket the code picker's mined vocabulary (`ikpu_terms`,
   *  `services/api/scripts/mine-ikpu-terms.ts`) groups lots by:
   *  - `'level2'` — the dotted `NN.N` / `NN.NN` head of the code (Uzbekistan's
   *    ЕНКТ, `25.30.11.110` → `25.30`);
   *  - `'ensTruClass'` — for a ЕНС ТРУ code `NNNNNN.NNN.NNNNNN`, its 6-digit
   *    class before the first dot (`329912.130.000000` → `329912`); a dotted
   *    code falls back to `'level2'` (Kazakhstan, where ~96 % of coded lots are
   *    ЕНС ТРУ and the dotted projection reached 4.3 % of them). */
  readonly vocabularyBucket: 'level2' | 'ensTruClass';
  resolve(code: string | null, title: string): string | null;
  classify(title: string): string | null;
}

/** The country's company-identifier scheme (ИНН/СТИР in UZ, a different
 *  scheme+shape elsewhere). `validate` says nothing about whether the taxpayer
 *  exists — only whether the string is syntactically usable (shape, not an
 *  obvious placeholder). See `uz.ts` for the UZ rules and why there is no
 *  check-digit verification. */
export interface CompanyIdConfig {
  readonly label: string;
  validate(id: string): boolean;
}

/**
 * The bar a company page must clear to be INDEXABLE when it is not grandfathered
 * (`services/api/src/modules/catalog/company-index-rule.ts`): at least
 * `minCompetitiveAwards` competitive awards (not a direct contract — see
 * `competitive-awards.ts`) inside the last `windowMonths` months, and — where
 * `requireActiveStatus` — `organizations.status_group = 'ACTIVE'`.
 *
 * UZ (owner, 2026-10-10): 2 in 24 months, ACTIVE in the soliq registry.
 * KZ (owner, 2026-10-15): 5 in 36 months — «страницы для большинства компаний, у
 * кого 5 и больше выигранных тендеров» — and no status test, because no KZ
 * register says ACTIVE: `gbd_ul` only ever sets LIQUIDATED, so `status_group` is
 * empty on every Kazakh organisation and an ACTIVE requirement would index nobody.
 *
 * Server-side only, like `sourceKeys`.
 */
export interface CompanyIndexConfig {
  readonly minCompetitiveAwards: number;
  readonly windowMonths: number;
  readonly requireActiveStatus: boolean;
  /**
   * Row ceiling on `/buyers/index` and `/suppliers/index` (per side) — a runaway-query
   * and disk guard, not the bar. Absent → the API's `INDEX_CAP` (40,000), which is
   * what UZ uses. KZ sets its own (2026-10-18): 53,309 suppliers clear its bar against
   * a 40,000 cap, so the cap had become the bar.
   */
  readonly indexCap?: number;
}

/**
 * Where an award price stops being credible, in the deployment's OWN currency
 * (so'm on UZ, tenge on KZ — the `*_uzs` columns hold local currency, the name
 * is historical). Read by the API's award guard
 * (`services/api/src/modules/catalog/award-sanity.ts` — `sanitizeAwardValue`,
 * `credibleAwardPrice()`: company totals, buyer/supplier pages, `/prices`,
 * market volume, the channel, the outcome copier) and by the data-plausibility
 * sweep (`plausibility-sweep.repository.ts`), which counts `tender_results` rows
 * with `winning_price_uzs < floor` or `>= ceiling`.
 *
 * UZ: 1,000 so'm / 10 трлн so'm — the literals the API carried before it read
 * them from here (specs pin them). KZ carries its own pair, chosen from the
 * live tenge price distribution (see `kz.ts`).
 *
 * Server-side only, like `sourceKeys`.
 */
export interface AwardPlausibilityConfig {
  /** Below this a winning price is not credible (strictly less-than). */
  readonly floor: number;
  /** At or above this a winning price is not credible. */
  readonly ceiling: number;
}

/**
 * The deployment's Telegram presence.
 *
 * UZ runs @uztender_com_bot (alerts, the login widget, the assistant Нигора) and
 * the public channel @uztender_uz. KZ runs its own bot @kaztender_com_bot (the
 * assistant Айгерим) since 2026-10-14 and has NO channel — a channel line or
 * footer must render nothing there rather than advertise the Uzbek one.
 */
export interface MessagingConfig {
  /** Whether the deployment runs a Telegram BOT: alerts, the login widget, the
   *  «подключить Telegram» prompts and the onboarding Telegram step. */
  readonly telegram: boolean;
  /** The bot's public username, without the @ — the one BotFather issued for
   *  this deployment. The server copy (/help) and the go-live check read it;
   *  the web bakes the same value from `VITE_TELEGRAM_BOT_USERNAME`, and the
   *  prod build refuses a mismatch. `null` when `telegram` is false. */
  readonly telegramBot: string | null;
  /** The public Telegram CHANNEL the copy advertises («📢 крупные тендеры каждый
   *  день»), or `null` for none. Not the bot and not the support contact. */
  readonly telegramChannel: { readonly handle: string; readonly url: string } | null;
  /** Whether a saved search that is created before any Telegram chat is linked
   *  gets its FIRST matching tender by e-mail. UZ: no — its activation moment is
   *  the Telegram connect, and an e-mail there would dedup-suppress the later
   *  Telegram send. KZ: yes — e-mail was its only channel until the bot, and a
   *  KZ account that never opens Telegram must still get the first alert. */
  readonly emailFirstAlert: boolean;
  /**
   * OPTIONAL — ONE first alert per saved search, and it fits the search (Spain,
   * negative QA 22.10 Н1). Two paths send the instant first lot: the create-time
   * send of `POST /me/saved-searches` and `POST /me/first-alert`, which the
   * onboarding sheet calls right after the save. The create-time one queried the
   * REQUEST's single fields, and an ИИ-подбор sends only plural ones (codes,
   * regions, words) next to its categories — so it mailed the newest lot of the
   * category anywhere in the country, and the first-alert call then mailed the
   * right one: two emails, the first one wrong, on 6 of 6 AI saves. Under this
   * switch the create-time send reads the SAVED row (its first code, region and
   * word — what `/me/first-alert` reads), and neither path sends a second first
   * lot under a search that already received one. Absent = UZ, as before; Kazakhstan sets it (2026-10-22, the same two emails
   * measured there: 1 of 34 first alerts in 10 days went out twice).
   */
  readonly singleFirstAlert?: boolean;
}

/** A payment mark the pricing page may print next to a pay button. */
export type PaymentMark =
  | 'payme'
  | 'click'
  | 'octobank'
  | 'uzcard'
  | 'humo'
  | 'visa'
  | 'mastercard'
  | 'stripe';

/** The legal entity that ACCEPTS card payments when it is not the operator
 *  named in the requisites (Kazakhstan: cards are charged by a US company
 *  through Stripe). Printed in the offer, the privacy policy and next to the
 *  pay button. */
export interface CardPayeeConfig {
  readonly name: string;
  readonly address: string;
  readonly phone: string;
  /** The processor the payee takes cards through — «Stripe». */
  readonly processor: string;
}

/**
 * How this deployment takes money. Read by the API (which plans are offered)
 * and by the web (what the pricing page renders) from the SAME object, so the
 * page can never show a way to pay the server would refuse.
 *
 * UZ is `local`: the Payme card sheet (card on file), hosted Payme / Click /
 * Octobank single payments, the yearly tab and the bank invoice — exactly what
 * it had before this block existed. KZ is `stripe`: ONE button per plan that
 * leaves for Stripe Checkout in subscription mode; no card form of ours, no
 * gateway chooser, no single payment, no yearly tab, no invoice (owner,
 * 2026-10-13).
 */
export interface PaymentsConfig {
  readonly checkout: 'local' | 'stripe';
  /** Yearly plans: the «Год» tab on /plans and the `*-yearly` keys in the
   *  server's offered list. */
  readonly yearlyPlans: boolean;
  /** «Оплата по счёту» (bank transfer against a numbered invoice). */
  readonly bankInvoice: boolean;
  /** Whether the `ip-*` tier is in the server's offered list at all. UZ keeps
   *  it there for its existing subscribers (the page hides the card); KZ has no
   *  such tier. */
  readonly ipPlan: boolean;
  /** Marks printed by the pay button. Nothing outside this list may be named
   *  on the deployment's pricing page. */
  readonly marks: readonly PaymentMark[];
  /** Who accepts card payments, when it is not the operator itself. */
  readonly cardPayee: CardPayeeConfig | null;
  /**
   * OPTIONAL — a bank invoice priced in US dollars on a Stripe deployment
   * (Kyrgyzstan, coordinator 2026-11-04): a transfer from Kyrgyzstan to the
   * Uzbek operator goes in USD by SWIFT only, so the yearly plans are invoiced
   * in dollars, with the som price beside them «справочно» at `kgsPerUsd`.
   * Only the plans listed in `amountsUsd` can be requested by invoice. Absent =
   * the Uzbek invoice (so'm, PDF) where `bankInvoice` is on — UZ unchanged.
   */
  readonly invoiceUsd?: InvoiceUsdConfig;
}

/** See {@link PaymentsConfig.invoiceUsd}. */
export interface InvoiceUsdConfig {
  /** Local currency units per US dollar the dollar amounts were computed at. */
  readonly kgsPerUsd: number;
  /** Who sets that rate — printed beside the reference amount («НБКР»). */
  readonly rateSource: string;
  /** Whole dollars per plan key: round(plan price / kgsPerUsd). */
  readonly amountsUsd: Readonly<Record<string, number>>;
}

/**
 * Money thresholds the server compares against, in the deployment's OWN
 * currency (so'm on UZ, tenge on KZ — the `*_uzs` columns hold local currency,
 * the name is historical). They were bare so'm literals in four files; each
 * UZ value below is that literal, unchanged.
 *
 * Server-side only, like `sourceKeys`.
 */
export interface ThresholdsConfig {
  /** Smallest amount ingestion treats as a real tender budget
   *  (`ingestion/normalize-helpers.ts`, `parseBudget`). Sources emit a
   *  placeholder like «1» for an undisclosed budget; anything below this is
   *  stored as «no budget». UZ 1 000 so'm. KZ 100 ₸ — the same floor the award
   *  guard uses (`awardPlausibility.floor`): below it sit symbolic 1–3 ₸ sums. */
  readonly minPlausibleBudget: number;
  /** A bid-package lot of kind `works` at or above this budget is «сложный
   *  ярус» and gets the expensive draft model
   *  (`bid-package/bid-package.models.ts`, `isComplexLot`). UZ 500 млн so'm
   *  (owner, 2026-09-26). KZ 100 млн ₸ — about the same real money
   *  (500 млн so'm ≈ $40k, 100 млн ₸ ≈ $190k is still a staged construction
   *  contract); a proposal, not an owner decision. */
  readonly complexLotBudget: number;
  /** The amount at which the stuck-checkout sweep splits its report
   *  (`billing/stuck-checkout-sweep.ts`): stuck orders at or above vs below.
   *  UZ 500 000 so'm (the 2026-08-14 anomaly). KZ 30 000 ₸ — between Pro
   *  (19 900) and Business (34 900) monthly, so the split separates the plans
   *  the way UZ's separates the monthly Pro from the annual/Business ones. */
  readonly stuckCheckoutBand: number;
}

/**
 * Which award rows (`tender_results`) are NOT a competition — contracts a buyer
 * signed with no competitive procedure, which the supplier-concentration reads
 * leave out (see `services/api/src/modules/catalog/competitive-awards.ts`).
 *
 * The two countries mark such a row differently, so the rule has two arms:
 *
 * - **by source** — the whole feed is direct purchases. UZ: `uzex-direct`
 *   (`GetDirectPurchases`); its rows carry no method at all.
 * - **by method** — one feed carries every procurement method and the row says
 *   which (`tender_results.method`). KZ: the goszakup contract registry and the
 *   goszakup results protocols hold «Из одного источника …» next to «Запрос
 *   ценовых предложений», «Открытый конкурс», … A row is non-competitive when
 *   its method contains one of the markers (case-insensitive substring). What a
 *   row with NO method is decides `unknownMethodIsCompetitive`.
 *
 * Server-side only, like `sourceKeys`.
 */
export interface CompetitionConfig {
  readonly nonCompetitiveSources: readonly string[];
  /** Lower-case substrings of `tender_results.method`. Empty = no method is a direct contract by name. */
  readonly nonCompetitiveMethodMarkers: readonly string[];
  /**
   * Does a row with no `method` count as a competition?
   *
   * UZ `true`: no Uzbek row carries a method, the source alone decides.
   * KZ `false`: where goszakup names the method, «из одного источника» is about
   * half of the rows that carry both БИНs, so a row whose method was never
   * harvested is as likely a direct contract as not — counting it would put
   * direct contracts back into the concentration read.
   *
   * The method arm is off only when there are no markers AND this is `true`
   * (UZ); then every read is exactly the source-only filter.
   */
  readonly unknownMethodIsCompetitive: boolean;
}

/** Full config (may reference Node-only deps once the classifier seam lands). */
export interface CountryConfig {
  readonly code: Country;
  readonly locales: readonly [string, ...string[]];
  readonly defaultLocale: string;
  /**
   * The locales offered to search engines — hreflang alternates, x-default, the
   * sitemap. Absent = every one of `locales` (UZ, and KZ until 2026-10-18).
   *
   * A locale left out still routes and renders for people, but its pages carry
   * `noindex` and no page advertises them. KZ `['ru']` (owner, 2026-10-18): 7 of
   * 504 visitors in 14 days had a Kazakh browser and Google sent 4 people to /kk,
   * so the Kazakh pages stopped being prerendered (`PRERENDER_LOCALES=ru`) — and
   * a ru page must then not name a /kk twin that would answer as a noindex shell.
   */
  readonly indexedLocales?: readonly [string, ...string[]];
  readonly regions: RegionCatalog;
  readonly currency: CurrencyConfig;
  readonly brand: BrandConfig;
  /** Country display name per UI locale, in the cases the copy needs. */
  readonly countryName: Record<string, CountryNameForms>;
  /** Web-form company-id descriptor (label + digit count) — public half of `companyId`. */
  readonly companyIdForm: CompanyIdFormConfig;
  /** Example phone number shown as the placeholder in the phone-capture input,
   *  in the country's own dialling format (UZ `+998 …`, KZ `+7 …`). Display only —
   *  the value the user types is validated server-side, not against this. */
  readonly phonePlaceholder: string;
  /**
   * The shape of a product code (`tenders.ikpu`, `ikpu_codes.code`) this
   * deployment accepts as a filter chip off untrusted input — the /results URL
   * (`ikpu=`), the card's `criteria=` JSON and the `results` saved search. A
   * value that does not match is dropped as «not a chip».
   *
   * UZ: the dotted ЕНКТ/ОКПД2 lineage, `26`, `26.20`, `26.20.16.170`.
   * KZ: ЕНС ТРУ `NNNNNN.NNN.NNNNNN` and its class / subclass prefixes
   * (`410010`, `410010.100`), plus the shorter dotted КПВЭД-style codes some
   * lots carry (`01.30.10`, `10.51.52.00.00.01.11.10.3`). The UZ pattern refused
   * every ЕНС ТРУ code, so a KZ code chip was silently dropped and the list came
   * back unfiltered (2026-10-17: «Щебень» 25 946 rows instead of 3).
   *
   * Public: the web parses the same URL the API sanitizes. No `g`/`y` flag — the
   * pattern is shared, and a stateful `lastIndex` would make `.test` alternate.
   */
  readonly productCodePattern: RegExp;
  /**
   * The shape of a catalog search query (`q`) that IS a product code rather
   * than words — answered as the `ikpu` prefix filter, not by full-text search
   * (`services/api/src/modules/catalog/code-query.ts`). Matched against the
   * trimmed query; anchored, no `g`/`y` flag (same reason as
   * `productCodePattern`).
   *
   * KZ: the ЕНС ТРУ class / subclass / position (`081212`, `081212.120`,
   * `081212.120.000031`) — full-text search found none of them (2026-10-22).
   * Absent = no query is treated as a code: UZ keeps its search exactly as it
   * was. Server-only — the web sends `q` as typed.
   */
  readonly searchCodePattern?: RegExp;
  /**
   * The IANA zone the deployment's people live in — the clock the web prints a
   * deadline / a renewal date in. Absent = `'Asia/Tashkent'`, which is what UZ
   * and KZ have always used (Kazakhstan moved to a single UTC+5 in 2024, the
   * same offset, so KZ keeps it byte for byte). Kyrgyzstan sets `'Asia/Bishkek'`
   * (UTC+6). Public: the web formats dates in it. Read through `deploymentTimeZone`.
   *
   * ⚠️ Only the web's date formatters read it so far; ~40 server files still
   * hard-code `Asia/Tashkent` (day windows, admin charts, quotas) — see the
   * Kyrgyzstan lane notes for the list.
   */
  readonly timeZone?: string;
  readonly analytics: AnalyticsConfig;
  /** Messaging channels this deployment offers for saved-search alerts and the
   *  login/connect surfaces. Public (on `PublicCountryConfig`): the web bundle
   *  reads it to gate those surfaces, and the prod build uses it to decide
   *  whether `VITE_TELEGRAM_BOT_USERNAME` is required. See {@link MessagingConfig}. */
  readonly messaging: MessagingConfig;
  /** Optional analytics/UX surfaces this deployment offers. `priceIntel` gates the
   *  three surfaces built on a real product-code (ИКПУ) taxonomy + price history:
   *  the `/market` dashboard, the `/prices` reference, and the goods/works/services
   *  `kind` filter. UZ has all three (years of `BidHistory` + the ИКПУ classifier);
   *  KZ's classifier is a stub with no price history, so they render near-empty and
   *  are hidden (`priceIntel: false`) rather than shown blank. Public (on
   *  `PublicCountryConfig`): the web bundle reads it to gate nav rows, the catalog
   *  `kind` filter, and the prerender/sitemap route list. `teamPlans` gates the
   *  seat-bearing «Бизнес» tier: UZ offers it; KZ does not for now (the server
   *  drops the `business-*` plans from the offered list, and the web never
   *  renders their card). */
  readonly features: {
    readonly priceIntel: boolean;
    readonly teamPlans: boolean;
    /**
     * Whether corporate-scope tenders must carry a buyer contact to appear in
     * browse (the contact gate). `true` where corporate sources publish a
     * contact (UZ banks). `false` where the only corporate source is
     * structurally contact-less (KZ mitwork lists a БИН and nothing else) — then
     * corporate is exempt from the gate like `ifi`/`supply`, otherwise the whole
     * layer is hidden. See `catalog-filters.ts` CONTACT_GATE.
     */
    readonly corporateContactGate: boolean;
    /**
     * Whether the `/start` and `/ip` conversion landings are served. They are
     * hand-written sales pages, not catalogue views: Uzbek prices in so'm, Uzbek
     * portals, «Оплата через Payme», the operator's phone. A deployment with no
     * copy of its own (KZ) must not serve — or prerender, or sitemap — the Uzbek
     * one under its own domain, so there the two paths redirect to the
     * catalogue. Turn it on for a country once that country's landing content
     * exists (`apps/web/src/content/landings`).
     */
    readonly conversionLandings: boolean;
    /**
     * ИИ-онбординг: the person describes in their own words what they supply,
     * and the server proposes a saved search grounded in this deployment's own
     * reference tables (categories, niches, product codes, regions) — the
     * onboarding search step and the «ИИ заполнит поля» block in the
     * saved-search builder. `true` = on for EVERY account, no PostHog flag.
     *
     * KZ: on (owner, 2026-10-15 «ИИ-онбординг врубай на КЗ»). UZ: off — the
     * Uzbek site keeps the category picker exactly as it was; with this off the
     * `/api/ai-search/*` endpoints answer 404 and the web never renders the step.
     */
    readonly aiOnboarding: boolean;
    /**
     * Pro teasers on the lot page promise only what Pro will show (owner's
     * complaint on KZ, 2026-10-15: «на превью данные есть, я покупаю, а здесь
     * их нет»): the market-data teaser blurs only metrics the comparables have,
     * the supplier-share / similar-winners teasers draw no more rows than the
     * data, the «Закрыто в Pro» card lists documents / contacts only when the
     * lot has them. `false` keeps the pre-2026-10-15 teasers byte for byte —
     * Uzbekistan's pre-purchase screen changes only on its owner's word.
     */
    readonly honestTeasers: boolean;
    /**
     * Whether this deployment ingests a `supply`-scope source (UZ: cooperation.uz
     * supply requests, `sourceKeys` has `cooperation`). Public, because the web
     * cannot read `sourceKeys`: with it off the search-result scope chips drop
     * «Кооперация» — KZ ingests no such source, so the chip could only ever
     * read 0 (phone QA, 2026-10-16). Pinned to `sourceKeys` by the country tests.
     */
    readonly supplyScope: boolean;
    /**
     * A refused one-time code empties the six boxes and puts the caret in the first
     * (ported from MiLicita `c1222fa43`, persona test 22.10 D7). The API throws
     * `UnauthorizedException('invalid_code')`, a STRING exception, which the HTTP
     * error filter wraps as `{ code: 'http_error', message: 'invalid_code' }`; with
     * this switch /login and /register recognise the refusal by its code OR its
     * message and call `OtpInputHandle.reset()`. A dropped connection or a 5xx still
     * keeps the typed code. OPTIONAL — absent or `false`: nothing is cleared, the
     * boxes keep the wrong digits, as before (UZ, KZ). Kyrgyzstan: on.
     */
    readonly refusedCodeCleared?: boolean;
  };
  /**
   * The international-institution (`ifi` scope) sources this deployment
   * ingests, as `TenderSource.key` values — the chips the `/ifi` page may draw.
   * Public, because the web cannot read `sourceKeys`: the chip list there was
   * one literal for both countries, so kaztender.com/ifi offered «UzRD» and
   * «ОБСЕ», Uzbek-only sources that could only ever read 0 (audit 2026-10-16).
   * Every key must also be in `sourceKeys` (refused by the schema otherwise).
   */
  readonly ifiSources: readonly string[];
  /**
   * The procurement platforms of the main (non-`ifi`) catalogue whose chips
   * `/catalog` may draw — «Все · Госзакупки · Самрук-Казына · …», the same
   * strip `/ifi` has for its institutions (owner, kaztender.com, 2026-10-19).
   * `TenderSource.key` values, in chip order; the API accepts `?source=` only
   * for these keys and ignores any other. Empty = no strip and no filter
   * (Uzbekistan: its catalogue stays exactly as it was). Every key must also
   * be in `sourceKeys` (refused by the schema otherwise).
   */
  readonly govSources: readonly string[];
  /** How this deployment takes money — see {@link PaymentsConfig}. Public: the
   *  pricing page and the API's offered-plan list read the same flags. */
  readonly payments: PaymentsConfig;
  /** `TenderSource.key` values this deployment ingests. Server-side only — the
   *  ingestion scheduler intersects its registered adapter keys against this
   *  list, so a source outside it is never enrolled even if its DB row is
   *  enabled. Deliberately NOT on `PublicCountryConfig`: which sources feed a
   *  deployment is not something the browser bundle needs or should ship. */
  readonly sourceKeys: readonly string[];
  /** Which award rows are direct contracts, not tenders — see
   *  {@link CompetitionConfig}. Server-side only. */
  readonly competition: CompetitionConfig;
  /** Company-identifier (ИНН/СТИР) validation. Server-side only — deliberately
   *  NOT on `PublicCountryConfig`: `validate` is a function, which cannot cross
   *  the server/browser-bundle boundary the public view exists for (and the
   *  browser has no business re-deriving this rule from a shared config either
   *  — see `services/api/src/modules/auth/validate-tin.ts`, the sole owner of
   *  server-side company-id checks). */
  readonly companyId: CompanyIdConfig;
  /** Product (ИКПУ) classifier. Server-side only — deliberately NOT on
   *  `PublicCountryConfig`: it is invoked at ingest to derive a lot's ИКПУ code,
   *  and its keyword tables must not enter the browser bundle. See
   *  `services/api/src/modules/ingestion/ikpu-classifier.ts` (which re-exports
   *  the UZ implementation for the ingestion pipeline). */
  readonly classifier: ClassifierAdapter;
  /** Indexability bar for company pages — see {@link CompanyIndexConfig}. Server-side only. */
  readonly companyIndex: CompanyIndexConfig;
  /** Credible award-price band, local currency — {@link AwardPlausibilityConfig}. Server-side. */
  readonly awardPlausibility: AwardPlausibilityConfig;
  /** Money thresholds, local currency — {@link ThresholdsConfig}. Server-side. */
  readonly thresholds: ThresholdsConfig;
  /**
   * Whether a lot's goods / works / services kind may come from the SOURCE
   * (`tenders.procurement_kind_source`, written by an adapter that publishes
   * it) ahead of the rule over the code's 2-digit section (the generated
   * `tenders.procurement_kind`). Server-side only.
   *
   * UZ `false`: no Uzbek source publishes a kind, the column stays NULL and
   * every kind filter / count / tab is the plain `procurement_kind` it always
   * was — the same SQL, the same index.
   * KZ `true`: goszakup publishes «Товар» / «Работа» / «Услуга» per announcement,
   * and the Uzbek section rule («41–43 и 71 = работы») is wrong for ЕНС ТРУ —
   * measured 2026-10-17 on 25 671 live goszakup lots: 2 975 got another kind
   * than the source's, 1 355 had no code and so no kind at all. Readers then
   * use `COALESCE(procurement_kind_source, procurement_kind)`
   * (`common/ikpu/procurement-kind.ts`).
   */
  readonly procurementKindFromSource: boolean;
  /**
   * How a product code's descendants are recognised when a code is used as a
   * filter, a saved-search / niche code or a facet parent. Server-side only.
   *
   * `'dotted'` (UZ, KZ): the codes carry dots, and a code WITHOUT a dot is a
   * whole segment — `26` matches `26` and `26.*`, never `260…`; a dotted code
   * matches by raw prefix (`26.2` ⊂ `26.20.16.170`). KZ's 6-digit ЕНС ТРУ class
   * (`081212`) is such a segment. Exactly the rule both have always had.
   * `'flat'` (KG): ОКГЗ is CPV-shaped — 8 digits, no dot, the hierarchy in the
   * leading digits (`45` → `452` → `4521` → `45210000`). A code matches by plain
   * string prefix, and a full 8-digit code stands for its node: its trailing
   * zeros are dropped first (`45210000` → `4521`, as the portals' own
   * `okgz/findAll` tree names it). Read through `codePrefixMode()`
   * (`services/api/src/common/ikpu/ikpu-hierarchy.ts`).
   */
  readonly productCodePrefix: 'dotted' | 'flat';
}

/** Web-safe view — the subset the browser bundle may import (no Node deps). */
export type PublicCountryConfig = Pick<
  CountryConfig,
  | 'code'
  | 'locales'
  | 'defaultLocale'
  | 'indexedLocales'
  | 'regions'
  | 'currency'
  | 'brand'
  | 'countryName'
  | 'companyIdForm'
  | 'phonePlaceholder'
  | 'productCodePattern'
  | 'timeZone'
  | 'analytics'
  | 'messaging'
  | 'features'
  | 'ifiSources'
  | 'govSources'
  | 'payments'
>;
// Widen the Pick as public fields are added.

/** The deployment's IANA time zone — `timeZone`, or `'Asia/Tashkent'` when absent. */
export function deploymentTimeZone(c: Pick<CountryConfig, 'timeZone'>): string {
  return c.timeZone ?? 'Asia/Tashkent';
}

export function assertNever(x: never): never {
  throw new Error(`Unhandled country/variant: ${String(x)}`);
}
